// Turns a raw loc field name into something readable, e.g. "project_scope" -> "project_scope"
const fieldLabelFromLoc = (loc) => {
  if (!Array.isArray(loc) || loc.length === 0) return 'field';
  // Drop the leading location marker ("body" / "query" / "path") when a real field follows.
  const parts = ['body', 'query', 'path', 'header', 'cookie'].includes(loc[0]) && loc.length > 1
    ? loc.slice(1)
    : loc;
  return parts.filter(part => typeof part !== 'number').join('.') || 'field';
};

// Converts a single FastAPI/Pydantic v2 validation item into a specific sentence.
const formatValidationItem = (item) => {
  if (!item || typeof item !== 'object') return String(item);
  const field = fieldLabelFromLoc(item.loc);
  const msg = (item.msg || '').replace(/^Value error,\s*/i, '').trim();

  switch (item.type) {
    case 'missing':
    case 'value_error.missing':
      return `${field} is missing`;
    case 'string_too_short':
      return `${field} is too short`;
    case 'string_too_long':
      return `${field} is too long`;
    case 'value_error':
    case 'assertion_error':
      return `${field}: ${msg || 'invalid value'}`;
    default:
      return msg ? `${field}: ${msg}` : `${field} is invalid`;
  }
};

// Extracts a specific, human-readable message from an axios error.
// Handles FastAPI 422 responses where `detail` is an array of validation objects,
// falling back to string details, axios's message, or a caller-provided default.
export const getErrorMessage = (error, fallback = 'An unexpected error occurred') => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const messages = detail.map(formatValidationItem).filter(Boolean);
    if (messages.length) return messages.join('; ');
  }

  if (typeof detail === 'string' && detail.trim()) return detail;

  // Some endpoints return { message } or { error } instead of { detail }.
  const altMessage = error?.response?.data?.message || error?.response?.data?.error;
  if (typeof altMessage === 'string' && altMessage.trim()) return altMessage;

  if (error?.message && !/^Request failed with status code/.test(error.message)) {
    return error.message;
  }

  return fallback;
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  return timeString.substring(0, 5);
};

export const getStatusLabel = (status) => {
  if (!status) return 'N/A';
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const calculateProgress = (completed, total) => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

export const calculateAttendanceRate = (attended, total) => {
  if (total === 0) return 0;
  return Math.round((attended / total) * 100);
};

export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const generateInitials = (firstName, lastName) => {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
};

export const sortByDate = (array, dateField, order = 'desc') => {
  return [...array].sort((a, b) => {
    const dateA = new Date(a[dateField]);
    const dateB = new Date(b[dateField]);
    return order === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

export const filterByStatus = (array, status) => {
  if (!status || status === 'all') return array;
  return array.filter(item => item.status === status);
};

export const searchByField = (array, searchTerm, fields) => {
  if (!searchTerm) return array;
  const term = searchTerm.toLowerCase();
  return array.filter(item => 
    fields.some(field => 
      item[field]?.toString().toLowerCase().includes(term)
    )
  );
};

export const calculateTotalBudget = (bomItems) => {
  return bomItems.reduce((total, item) => total + (item.cost * item.quantity), 0).toFixed(2);
};

export const groupByField = (array, field) => {
  return array.reduce((groups, item) => {
    const key = item[field];
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
};

export const isUpcoming = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

export const isPastDeadline = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

export const downloadMarkdown = (content, filename) => {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const sanitizePdfText = (value) => String(value ?? '')
  .replace(/[“”]/g, '"')
  .replace(/[‘’]/g, "'")
  .replace(/[–—]/g, '-')
  .replace(/•/g, '-')
  .replace(/[^\x20-\x7E]/g, '');

const escapePdfText = (value) => sanitizePdfText(value)
  .replace(/\\/g, '\\\\')
  .replace(/\(/g, '\\(')
  .replace(/\)/g, '\\)');

const wrapPdfLine = (text, maxChars) => {
  const words = sanitizePdfText(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let current = '';

  words.forEach(word => {
    if (word.length > maxChars) {
      if (current) {
        lines.push(current);
        current = '';
      }
      for (let index = 0; index < word.length; index += maxChars) {
        lines.push(word.slice(index, index + maxChars));
      }
      return;
    }

    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
};

const createPdfBlob = (documentData) => {
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const marginX = 48;
  const topY = 790;
  const bottomY = 54;
  const contentWidth = pageWidth - (marginX * 2);
  const pages = [[]];
  let y = topY;

  const ensureSpace = (height) => {
    if (y - height >= bottomY) return;
    pages.push([]);
    y = topY;
  };

  const addText = (text, options = {}) => {
    const size = options.size || 11;
    const font = options.bold ? 'F2' : 'F1';
    const lineHeight = options.lineHeight || Math.round(size * 1.45);
    const indent = options.indent || 0;
    const maxChars = Math.max(18, Math.floor((contentWidth - indent) / (size * 0.52)));
    const lines = String(text ?? '').split('\n').flatMap(line => wrapPdfLine(line, maxChars));

    lines.forEach(line => {
      ensureSpace(lineHeight);
      pages[pages.length - 1].push({ text: line, x: marginX + indent, y, size, font });
      y -= lineHeight;
    });
  };

  const addGap = (height) => {
    ensureSpace(height);
    y -= height;
  };

  addText(documentData.title || 'Report', { size: 20, bold: true, lineHeight: 26 });
  if (documentData.subtitle) {
    addText(documentData.subtitle, { size: 11, lineHeight: 16 });
  }
  addGap(8);

  if (documentData.meta?.length) {
    documentData.meta.forEach(item => addText(`${item.label}: ${item.value || 'Not specified'}`, { size: 10, bold: true, lineHeight: 14 }));
    addGap(8);
  }

  documentData.sections?.forEach(section => {
    addText(section.title, { size: 14, bold: true, lineHeight: 20 });
    if (section.body) addText(section.body, { size: 11, lineHeight: 16 });
    section.items?.forEach(item => addText(`- ${item}`, { size: 11, lineHeight: 16, indent: 14 }));
    section.subsections?.forEach(subsection => {
      addGap(4);
      addText(subsection.title, { size: 11, bold: true, lineHeight: 16 });
      if (subsection.body) addText(subsection.body, { size: 11, lineHeight: 16 });
      subsection.items?.forEach(item => addText(`- ${item}`, { size: 11, lineHeight: 16, indent: 14 }));
    });
    addGap(10);
  });

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'
  ];

  const pageObjectNumbers = [];
  const contentObjectNumbers = [];

  pages.forEach(page => {
    const content = page.map(line => (
      `BT /${line.font} ${line.size} Tf ${line.x.toFixed(2)} ${line.y.toFixed(2)} Td (${escapePdfText(line.text)}) Tj ET`
    )).join('\n');

    const pageObjectNumber = objects.length + 1;
    const contentObjectNumber = objects.length + 2;
    pageObjectNumbers.push(pageObjectNumber);
    contentObjectNumbers.push(contentObjectNumber);

    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObjectNumber} 0 R >>`,
      `<< /Length ${content.length} >>\nstream\n${content}\nendstream`
    );
  });

  objects[1] = `<< /Type /Pages /Kids [${pageObjectNumbers.map(number => `${number} 0 R`).join(' ')}] /Count ${pageObjectNumbers.length} >>`;

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  for (let index = 1; index <= objects.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

export const downloadPdfDocument = (documentData, filename = 'report') => {
  const safeFilename = String(filename).replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'report';
  const blob = createPdfBlob(documentData);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeFilename}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
